import { useEffect, useRef, useState } from "react";
import { IdaEntityType, IdaGenerateMode, useIdaGenerateMutation } from "../../../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";
import { EventFormState } from "../../eventMockData";
import { STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";

export type EventIdaField = "heroShortDescription" | "description";

export const EVENT_IDA_FIELDS = {
  heroShortDescription: { label: "Hero Banner Description", maxLength: 250 },
  description: { label: "Event Description", maxLength: 500 },
} as const;

const plainText = (html: string): string => {
  const document = new DOMParser().parseFromString(html, "text/html");
  return (document.body.textContent || "").trim();
};

export const buildEventIdaDetails = (event: EventFormState): string => [
  event.eventType && `Event type: ${event.eventType}`,
  event.startDate && `Start: ${event.startDate} ${event.startTime || ""}`,
  event.endDate && `End: ${event.endDate} ${event.endTime || ""}`,
  event.inPersonEnabled && event.venueName && `Venue: ${event.venueName}`,
  event.virtualEnabled && "Online attendance is available.",
  event.heroShortDescription && `Hero description: ${plainText(event.heroShortDescription)}`,
  event.description && `Event description: ${plainText(event.description)}`,
  ...(event.speakers || []).map((speaker) => `Speaker: ${speaker.name}`),
  ...(event.agendaItems || []).map((item) => `Agenda: ${item.timeLabel} ${plainText(item.details)}`),
].filter(Boolean).join("\n");

export const useEventIdaGeneration = ({ event, onUse }: {
  event: EventFormState;
  onUse: (field: EventIdaField, html: string) => void;
}) => {
  const [generateText] = useIdaGenerateMutation({
    context: getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: [] }),
  });
  const [field, setField] = useState<EventIdaField | null>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);

  const generate = async (nextField: EventIdaField) => {
    if (inFlight.current) return;
    const requestId = ++request.current;
    const keyDetails = buildEventIdaDetails(event);
    setField(nextField);
    setValue("");
    setError("");
    // Same limits as before; each cause has its own text (wave 3, EV-034).
    if (!event.title.trim()) {
      setError("Enter the Event Title first. IDA uses it to write the text.");
      return;
    }
    if (event.title.trim().length > 200) {
      setError("The Event Title is longer than 200 characters. Shorten it, then try again.");
      return;
    }
    if (keyDetails.length > 12000) {
      setError("The event details are too long for IDA. Shorten the description, agenda or speaker details, then try again.");
      return;
    }
    inFlight.current = true;
    setLoading(true);
    try {
      const result = await generateText({ variables: { input: {
        entityName: event.title.trim(),
        entityType: IdaEntityType.Event,
        mode: IdaGenerateMode.EventDescription,
        keyDetails,
        maxLength: EVENT_IDA_FIELDS[nextField].maxLength,
      } } });
      const generated = result.data?.idaGenerate;
      if (!generated?.success || !generated.generatedText.trim()) throw new Error("No description returned");
      if (request.current === requestId) setValue(generated.generatedText.trim().slice(0, EVENT_IDA_FIELDS[nextField].maxLength));
    } catch {
      if (request.current === requestId) setError(`IDA could not write the ${EVENT_IDA_FIELDS[nextField].label}. Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const close = () => {
    request.current += 1;
    setField(null);
  };
  const useDescription = () => {
    if (!field || loading || !value.trim() || error) return;
    // Provider text remains text when it enters the rich-text editor.
    const paragraph = document.createElement("p");
    paragraph.textContent = value.trim().slice(0, EVENT_IDA_FIELDS[field].maxLength);
    onUse(field, paragraph.outerHTML);
    close();
  };
  const editValue = (text: string) => {
    setValue(text);
    setError("");
  };
  return { field, value, setValue: editValue, error, loading, generate, close, useDescription };
};
